import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { required } from 'redux-form-validators';
import { FormattedMessage, injectIntl } from 'react-intl';
import { DefaultButton, ReduxSelectField } from 'common/components';
import { editUserPartnerAccount } from '../../_redux';

import { staticStyles } from './style';

// 检查重复项的函数，仅在提交时使用
const checkDuplicateItems = (partnershipConditions, partnershipConditionsList) => {
  // 如果没有选择或只选择了一个条件，不需要校验
  if (!partnershipConditions || !Array.isArray(partnershipConditions) || partnershipConditions.length <= 1) {
    return undefined;
  }

  // 创建一个统一的映射来存储所有已使用的program值及其来源字段类型
  // key: program值, value: {字段类型, 条件索引}
  const allProgramsMap = new Map();
  const allMasterProgramsMap = new Map();

  // 遍历查找重复项
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < partnershipConditions.length; i++) {
    const conditionId = partnershipConditions[i];
    // 找到对应的条件详情
    const condition = partnershipConditionsList.find(cond => cond.id === conditionId);

    // eslint-disable-next-line no-continue
    if (!condition) continue;

    // 检查table_program是否与任何已存在的program重复
    if (condition.tableProgram) {
      if (allProgramsMap.has(condition.tableProgram)) {
        const existingProgram = allProgramsMap.get(condition.tableProgram);
        if (existingProgram.title !== condition.title) {
          return `Duplicate IB Programs:${existingProgram.title} & ${condition.title}`;
        }
      }
      allProgramsMap.set(condition.tableProgram, {
        fieldType: 'table_program',
        conditionIndex: i,
        title: condition.title,
      });
    }

    // 检查a_book_program是否与任何已存在的program重复
    if (condition.aBookProgram) {
      if (allProgramsMap.has(condition.aBookProgram)) {
        const existingProgram = allProgramsMap.get(condition.aBookProgram);
        if (existingProgram.title !== condition.title) {
          return `Duplicate IB Programs:${existingProgram.title} & ${condition.title}`;
        }
      }
      allProgramsMap.set(condition.aBookProgram, {
        fieldType: 'a_book_program',
        conditionIndex: i,
        title: condition.title,
      });
    }

    // 检查b_book_program是否与任何已存在的program重复
    if (condition.bBookProgram) {
      if (allProgramsMap.has(condition.bBookProgram)) {
        const existingProgram = allProgramsMap.get(condition.bBookProgram);
        if (existingProgram.title !== condition.title) {
          return `Duplicate IB Programs:${existingProgram.title} & ${condition.title}`;
        }
      }
      allProgramsMap.set(condition.bBookProgram, {
        fieldType: 'b_book_program',
        conditionIndex: i,
        title: condition.title,
      });
    }

    // 检查master_reward_table是否与任何已存在的program重复
    if (condition.masterRewardTableProgram) {
      if (allMasterProgramsMap.has(condition.masterRewardTableProgram)) {
        const existingProgram = allMasterProgramsMap.get(condition.masterRewardTableProgram);
        return `Duplicate Master IB Programs:${existingProgram.title} & ${condition.title}`;
      }
      allMasterProgramsMap.set(condition.masterRewardTableProgram, {
        fieldType: 'master_table_program',
        conditionIndex: i,
        title: condition.title,
      });
    }
    console.log('all', i, allMasterProgramsMap);
    // 检查master_a_book_table是否与任何已存在的program重复
    if (condition.masterABookTableProgram) {
      if (allMasterProgramsMap.has(condition.masterABookTableProgram)) {
        const existingProgram = allMasterProgramsMap.get(condition.masterABookTableProgram);
        return `Duplicate Master IB Programs:${existingProgram.title} & ${condition.title}`;
      }
      allMasterProgramsMap.set(condition.masterABookTableProgram, {
        fieldType: 'master_a_book_program',
        conditionIndex: i,
        title: condition.title,
      });
    }
    console.log('a', i, allMasterProgramsMap);
    // 检查master_b_book_table是否与任何已存在的program重复
    if (condition.masterBBookTableProgram) {
      if (allMasterProgramsMap.has(condition.masterBBookTableProgram)) {
        const existingProgram = allMasterProgramsMap.get(condition.masterBBookTableProgram);
        return `Duplicate Master IB Programs:${existingProgram.title} & ${condition.title}`;
      }
      allMasterProgramsMap.set(condition.masterBBookTableProgram, {
        fieldType: 'master_b_book_program',
        conditionIndex: i,
        title: condition.title,
      });
    }
    console.log('b', i, allMasterProgramsMap);
  }

  return undefined; // 校验通过
};

const UserPartnerAccountEditForm = ({
  accountId,
  rewardFrequencies,
  partnershipConditionsList,
  partnerCode,
  userId,
  handleSubmit,
  pristine,
  submitting,
  invalid,
  dispatch,
  intl,
  error,
  onSubmitSuccess,
  getPartnershipConditionsList,
}) => {
  const submit = handleSubmit(values => {
    // 在提交时检查重复项
    const duplicateError = checkDuplicateItems(values.partnershipConditions, partnershipConditionsList);
    if (duplicateError) {
      // 显示错误提示
      alert(duplicateError);
      // 返回一个rejected的Promise阻止表单提交
      return Promise.reject(new Error(duplicateError));
    }

    // 将partnershipConditions重命名为partnership_conditions_multiple，符合后端要求
    const { partnershipConditions, ...restValues } = values;
    const payload = { ...restValues, accountId, partnershipConditionsMultiple: partnershipConditions };

    // 使用Promise包装dispatch调用，以便在成功后执行回调
    return new Promise((resolve, reject) => {
      editUserPartnerAccount(
        {
          ...payload,
          // 添加onSuccess和onError回调
          onSuccess: () => {
            if (onSubmitSuccess) {
              onSubmitSuccess();
            }
            resolve();
          },
          onError: error => {
            reject(error);
          },
        },
        dispatch
      );
    });
  });

  const REWARD_FREQUENCIES = {
    dailyInstant: intl.formatMessage({ id: 'justDailyInstant' }),
    monthlyInstant: intl.formatMessage({ id: 'justMonthlyInstant' }),
    monthlyDelayed: intl.formatMessage({ id: 'justMonthlyDelayed' }),
  };

  return (
    <form className="UserPartnerAccountEditForm" onSubmit={submit}>
      <FormattedMessage id="editPartnerAccountDescription" values={{ partnerCode }}>
        {txt => <p className="UserPartnerAccountEditForm__description">{txt}</p>}
      </FormattedMessage>
      <div className="UserPartnerAccountEditForm__inputs-inner">
        <Field
          component={ReduxSelectField}
          name="partnershipConditions"
          maxMenuHeight={100}
          textId="rewardTemplate"
          options={partnershipConditionsList.map(cond => ({
            value: cond.id,
            label: cond.title,
          }))}
          validate={[required()]}
          disabled={submitting}
          searchable
          multi
          onMenuOpen={() => {
            // 每次打开下拉时获取最新模板列表
            if (getPartnershipConditionsList && userId) {
              getPartnershipConditionsList(userId);
            }
          }}
        />

        <Field
          component={ReduxSelectField}
          name="frequencyOfPayments"
          maxMenuHeight={100}
          textId="justFrequencyOfReward"
          options={rewardFrequencies.map(freq => ({
            value: freq,
            label: REWARD_FREQUENCIES[freq] ?? '',
          }))}
          validate={[required()]}
          disabled={submitting}
        />
      </div>

      <DefaultButton
        textId="justChange"
        type="submit"
        loading={submitting}
        disabled={invalid || pristine || submitting}
        filled
      />
      {error && <strong className="formError">{error}</strong>}

      <style jsx>{staticStyles}</style>
    </form>
  );
};

UserPartnerAccountEditForm.propTypes = {
  accountId: PropTypes.number.isRequired,
  rewardFrequencies: PropTypes.array.isRequired,
  partnershipConditionsList: PropTypes.array.isRequired,
  partnerCode: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  error: PropTypes.string,
  onSubmitSuccess: PropTypes.func,
  getPartnershipConditionsList: PropTypes.func.isRequired,
};

UserPartnerAccountEditForm.defaultProps = {
  error: '',
  onSubmitSuccess: null,
};

export default reduxForm({
  form: 'user-partner-account-edit-form',
})(injectIntl(UserPartnerAccountEditForm));
export { UserPartnerAccountEditForm };
